import { useMemo } from 'react';
import { useHorizontalScrollAffordance } from '../../hooks/useHorizontalScrollAffordance';
import type { Province } from '../../types/province';
import { StatCard } from '../ui/StatCard';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { ModCell } from '../economy/FlowSection';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { RACE_UNIT_STATS } from '../../constants/game';
import { ordinalSuffix } from '../throne/throneFormatting';
import {
  buildStatCards,
  buildCompactStatRows,
  buildArmyComposition,
  buildOffenseRows,
  buildDefenseRows,
  buildModifiers,
  buildTrainingPlannerData,
  MILITARY_SOURCE_COLORS,
  type StatCardData,
  type MetricRowData,
  type ArmyUnitKind,
  type ArmyCompositionEntry,
  type MilitaryFlowRow,
  type MilitaryModifier,
} from './trainingOverviewBuilders';

/**
 * SF-162: merged Overview tab for `Training.tsx` — ports/restructures the deleted `council/tabs/
 * TroopsTab.tsx` + `MilitaryPowerTab.tsx` content into four sections (stat cards, Army
 * Composition, Offense/Defense breakdown + Active Modifiers, training planner). One shared file
 * with a `compact` prop, following `SciencesOverviewTab.tsx`'s precedent (SF-161) — not a
 * `Mobile*`/`Desktop*` file pair. See spec Component 1 / Key Implementation Note 3.
 */
export interface TrainingOverviewTabProps {
  province: Province;
  compact: boolean;
  getUnitStats: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => { offense: number; defense: number };
  getUnitName: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => string;
}

export function TrainingOverviewTab({ province: p, compact, getUnitStats, getUnitName }: TrainingOverviewTabProps) {
  const race = p.metadata.race;

  const stats = useMemo(() => buildStatCards(p), [p]);
  const compactStats = useMemo(() => buildCompactStatRows(p), [p]);
  const composition = useMemo(() => buildArmyComposition(p), [p]);
  const offense = useMemo(() => buildOffenseRows(p), [p]);
  const defense = useMemo(() => buildDefenseRows(p), [p]);
  const modifiers = useMemo(() => buildModifiers(p), [p]);

  const armyCards = composition.map((entry) => (
    <ArmyCard
      key={entry.key}
      entry={entry}
      race={race}
      getUnitName={getUnitName}
      getUnitStats={getUnitStats}
      tickIntervalMs={p.sync.tickIntervalMs}
    />
  ));

  if (compact) {
    return (
      <div className="flex flex-col gap-3">
        <CompactStatCard label="Combat Power" rows={compactStats.combatPower} />
        <CompactStatCard label="Status" rows={compactStats.status} />

        <GlanceCard title="Army Composition">
          <div className="flex flex-col gap-3">{armyCards}</div>
        </GlanceCard>

        <MilitaryFlowSectionCompact title="Offense Breakdown" rows={offense.rows} color="#c45a4a" />
        <MilitaryFlowSectionCompact title="Defense Breakdown" rows={defense.rows} color="#4a7ac4" />
        <ActiveModifiersSection modifiers={modifiers} compact />

        <TrainingPlannerSection training={p.military.training} race={race} compact />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <StatRow label="Combat Power" cards={stats.combatPower} />
        <StatRow label="Status" cards={stats.status} />
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.text.primary }}>
          Army Composition
        </h2>
        <div className="flex flex-wrap gap-3">{armyCards}</div>
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.text.primary }}>
          Offense/Defense Breakdown
        </h2>
        <div className="space-y-3">
          <MilitaryFlowSection title="Offense Breakdown" rows={offense.rows} color="#c45a4a" />
          <MilitaryFlowSection title="Defense Breakdown" rows={defense.rows} color="#4a7ac4" />
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.text.primary }}>
          Active Modifiers
        </h2>
        <ActiveModifiersSection modifiers={modifiers} compact={false} />
      </div>

      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.text.primary }}>
          Training Planner
        </h2>
        <TrainingPlannerSection training={p.military.training} race={race} compact={false} />
      </div>
    </div>
  );
}

// ─── Stat cards ─────────────────────────────────────────────────────────────

function StatRow({ label, cards }: { label: string; cards: StatCardData[] }) {
  return (
    <div data-testid={`stat-row-${label === 'Combat Power' ? 'combat-power' : 'status'}`}>
      <div className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: COLORS.text.tertiary }}>
        {label}
      </div>
      <div className="flex gap-3">
        {cards.map((c) => (
          <div key={c.key} className="w-36">
            <StatCard label={c.label} value={c.value} sub={c.sub} borderless />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mobile-only — `MetricRow`-shaped rows (bare numeric value + separate unit), not the desktop
 *  `StatCard`'s pre-formatted-string rows (CLAUDE.md label-unit-value layout). */
function CompactStatCard({ label, rows }: { label: string; rows: MetricRowData[] }) {
  return (
    <GlanceCard title={label}>
      {rows.map((r, idx) => (
        <MetricRow key={r.key} label={r.label} value={r.value} unit={r.unit} weight={idx === 0 ? 'primary' : 'secondary'} />
      ))}
    </GlanceCard>
  );
}

// ─── Army Composition ────────────────────────────────────────────────────────

/**
 * Resolves each unit kind's display label + `(off/def)` suffix. Specialist/elite kinds read the
 * threaded live `getUnitStats`/`getUnitName` props (same `raceConfig`-backed source the Train tab
 * uses — Phase 3 Decision 4). Soldiers has no live equivalent (`RaceConfig.units` only covers the
 * three trainable specialist/elite kinds), so it reads the static `RACE_UNIT_STATS` constant
 * directly — not a competing duplicate of a live source, since none exists for Soldiers. War
 * Horses/Generals carry no off/def pair at all (Component 1b/Note 9).
 */
function resolveUnitDisplay(
  kind: ArmyUnitKind,
  race: string,
  getUnitName: TrainingOverviewTabProps['getUnitName'],
  getUnitStats: TrainingOverviewTabProps['getUnitStats'],
): { label: string; suffix: string | null } {
  switch (kind) {
    case 'offenseSpecialist': {
      const s = getUnitStats('OFFENSE_SPECIALIST', 'offenseSpecialist');
      return { label: getUnitName('OFFENSE_SPECIALIST', 'offenseSpecialist'), suffix: `(${s.offense}/${s.defense})` };
    }
    case 'defenseSpecialist': {
      const s = getUnitStats('DEFENSE_SPECIALIST', 'defenseSpecialist');
      return { label: getUnitName('DEFENSE_SPECIALIST', 'defenseSpecialist'), suffix: `(${s.offense}/${s.defense})` };
    }
    case 'elite': {
      const s = getUnitStats('ELITE', 'elite');
      return { label: getUnitName('ELITE', 'elite'), suffix: `(${s.offense}/${s.defense})` };
    }
    case 'soldier': {
      const s = RACE_UNIT_STATS[race?.toUpperCase()] || RACE_UNIT_STATS.HUMAN;
      return { label: 'Soldiers', suffix: `(${s.soldier.off}/${s.soldier.def})` };
    }
    case 'warHorses':
      return { label: 'War Horses', suffix: null };
  }
}

function ArmyCard({
  entry,
  race,
  getUnitName,
  getUnitStats,
  tickIntervalMs,
}: {
  entry: ArmyCompositionEntry;
  race: string;
  getUnitName: TrainingOverviewTabProps['getUnitName'];
  getUnitStats: TrainingOverviewTabProps['getUnitStats'];
  tickIntervalMs: number;
}) {
  const heading = entry.isHome ? 'Home' : `${entry.generalNumber}${ordinalSuffix(entry.generalNumber!)} Army`;

  return (
    <div
      className="rounded p-3 flex flex-col gap-1.5"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, flexBasis: '240px', minWidth: '240px' }}
    >
      <div className="text-sm font-semibold" style={{ color: COLORS.text.primary }}>
        {heading}
      </div>

      {entry.isHome ? (
        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
          Generals {formatNumber(entry.generalsAvailable ?? 0)} / {formatNumber(entry.generalsTotal ?? 0)} available
        </div>
      ) : (
        <div className="text-xs font-mono" style={{ color: COLORS.text.tertiary }}>
          {formatDuration(entry.ticksRemaining ?? 0, { returnTimeFraction: entry.returnTimeFraction ?? undefined, tickIntervalMs })} left
        </div>
      )}

      {entry.units.length === 0 ? (
        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>No units.</div>
      ) : (
        entry.units.map((row) => {
          const { label, suffix } = resolveUnitDisplay(row.kind, race, getUnitName, getUnitStats);
          return (
            <div key={row.kind} className="flex items-center justify-between text-sm">
              <span style={{ color: COLORS.text.primary }}>
                {label}
                {suffix && <span style={{ color: COLORS.text.tertiary }}> {suffix}</span>}
              </span>
              <span className="font-mono" style={{ color: COLORS.text.secondary }}>{formatNumber(row.count)}</span>
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Offense/Defense Breakdown ───────────────────────────────────────────────
// Phase 3 Decision 6: not merged into SF-164's `FlowSection` (structurally different Count/Pts/
// Total grid) — kept as a local, renamed component. Desktop is a near-verbatim port; mobile is
// restructured as stacked rows mirroring `TechnologyTable`'s compact shape (Component 1d).

/** SF-180: the final Modified Offense/Defense row's `value` is `null` only when the source DTO
 *  field is absent (pre-deploy Redis cache) — render "—", never coerce to 0. */
function formatFlowRowValue(row: MilitaryFlowRow): string {
  if (row.value === null) return '—';
  return `${formatNumber(Math.round(row.value))} ${row.unit}`;
}

function MilitaryFlowSection({ title, rows, color }: { title: string; rows: MilitaryFlowRow[]; color: string }) {
  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
      <div className="grid items-center px-3 py-1.5" style={{ gridTemplateColumns: '1fr 80px 50px 100px', backgroundColor: COLORS.sectionHeader }}>
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{title}</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Count</span>
        <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Pts</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Total</span>
      </div>

      {rows.map((row, idx) => {
        const valueColor = row.isTotal ? color : row.isSubtotal ? COLORS.text.primary : COLORS.text.secondary;
        return (
          <div
            key={idx}
            className="grid items-center px-3 py-1.5"
            style={{
              gridTemplateColumns: '1fr 80px 50px 100px',
              borderBottom: `1px solid ${COLORS.border}`,
              backgroundColor: row.isTotal ? COLORS.sectionHeader : row.isSubtotal ? 'rgba(255,255,255,0.02)' : undefined,
              opacity: row.dimmed ? 0.4 : 1,
            }}
          >
            <span
              className={`text-sm ${row.isTotal || row.isSubtotal ? 'font-semibold' : ''}`}
              style={{ color: row.indent ? MILITARY_SOURCE_COLORS[row.source] : COLORS.text.primary, paddingLeft: row.indent ? '16px' : undefined }}
            >
              {row.indent && (
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ backgroundColor: MILITARY_SOURCE_COLORS[row.source] }} />
              )}
              {row.label}
            </span>
            <span className="text-right text-sm font-mono" style={{ color: COLORS.text.tertiary }}>
              {row.indent || row.isSubtotal || row.isTotal ? '' : (row.count !== undefined ? formatNumber(row.count) : '')}
            </span>
            <span className="text-center text-sm font-mono" style={{ color: COLORS.text.tertiary }}>
              {row.indent || row.isSubtotal || row.isTotal ? '' : (row.multiplier ?? '')}
            </span>
            <span className={`text-right text-sm font-mono ${row.isTotal || row.isSubtotal ? 'font-semibold' : ''}`} style={{ color: valueColor }}>
              {row.indent ? row.unit : formatFlowRowValue(row)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MilitaryFlowSectionCompact({ title, rows, color }: { title: string; rows: MilitaryFlowRow[]; color: string }) {
  return (
    <GlanceCard title={title}>
      {rows.map((row, idx) => {
        const valueColor = row.isTotal ? color : row.isSubtotal ? COLORS.text.primary : COLORS.text.secondary;
        return (
          <div key={idx} className="flex flex-col gap-0.5" style={{ opacity: row.dimmed ? 0.4 : 1 }}>
            <div className="flex items-center justify-between gap-2">
              <span
                className="text-sm flex items-center gap-1.5"
                style={{
                  color: row.indent ? MILITARY_SOURCE_COLORS[row.source] : row.isTotal || row.isSubtotal ? COLORS.text.primary : COLORS.text.secondary,
                  fontWeight: row.isTotal || row.isSubtotal ? 600 : undefined,
                }}
              >
                {row.indent && (
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: MILITARY_SOURCE_COLORS[row.source] }} />
                )}
                {row.label}
              </span>
              <span className="text-sm font-mono" style={{ color: valueColor }}>
                {row.indent ? row.unit : formatFlowRowValue(row)}
              </span>
            </div>
            {!row.indent && !row.isSubtotal && !row.isTotal && (
              <div className="text-[11px]" style={{ color: COLORS.text.tertiary }}>
                {row.count !== undefined ? formatNumber(row.count) : ''} {row.multiplier ?? ''}
              </div>
            )}
          </div>
        );
      })}
    </GlanceCard>
  );
}

// ─── Active Modifiers ────────────────────────────────────────────────────────
// `ModCell` reused verbatim from `economy/FlowSection.tsx` (Phase 3 Decision 6) — its `{text,
// negative}` shape is exactly what this table's three columns need, fixing the
// `value.startsWith('-')` string-sniffing bug in the process.

function ActiveModifiersSection({ modifiers, compact }: { modifiers: MilitaryModifier[]; compact: boolean }) {
  if (compact) {
    return (
      <GlanceCard title="Active Modifiers">
        {modifiers.length === 0 ? (
          <div className="text-sm" style={{ color: COLORS.text.tertiary }}>No active modifiers.</div>
        ) : (
          modifiers.map((mod, idx) => (
            <div key={idx} className="flex flex-col gap-1">
              <div className="text-sm flex items-center gap-1.5" style={{ color: COLORS.text.primary }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: MILITARY_SOURCE_COLORS[mod.source] }} />
                {mod.name}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span style={{ color: COLORS.text.tertiary }}>Off</span>
                <ModCell value={mod.offense} />
                <span style={{ color: COLORS.text.tertiary }}>Def</span>
                <ModCell value={mod.defense} />
                <span style={{ color: COLORS.text.tertiary }}>Other</span>
                <ModCell value={mod.other} />
              </div>
            </div>
          ))
        )}
      </GlanceCard>
    );
  }

  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
      <div className="grid items-center px-3 py-1.5" style={{ gridTemplateColumns: '180px 120px 120px 1fr', backgroundColor: COLORS.sectionHeader }}>
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Active Modifiers</span>
        <span className="text-center text-xs uppercase tracking-wide" style={{ color: '#c45a4a' }}>Offense</span>
        <span className="text-center text-xs uppercase tracking-wide" style={{ color: '#4a7ac4' }}>Defense</span>
        <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>Other</span>
      </div>

      {modifiers.length === 0 ? (
        <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>No active modifiers.</div>
      ) : (
        modifiers.map((mod, idx) => (
          <div
            key={idx}
            className="grid items-center px-3 py-1.5"
            style={{ gridTemplateColumns: '180px 120px 120px 1fr', borderBottom: `1px solid ${COLORS.border}` }}
          >
            <span className="text-sm flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: MILITARY_SOURCE_COLORS[mod.source] }} />
              <span style={{ color: COLORS.text.primary }}>{mod.name}</span>
            </span>
            <ModCell value={mod.offense} />
            <ModCell value={mod.defense} />
            <ModCell value={mod.other} />
          </div>
        ))
      )}
    </div>
  );
}

// ─── Training Planner ────────────────────────────────────────────────────────
// Reuses `SciencesOverviewTab.tsx`'s `InvestmentPlannerCard` mobile mechanics directly (Component
// 1c / Key Implementation Note 4) — sticky first column, `overflowX: auto` + `tabIndex={0}`,
// `compact` day-filtering. Same shape on both platforms; unlike the other three sections, this one
// stays a bordered card rather than a `GlanceCard` on mobile, matching the Sciences precedent.

function TrainingPlannerSection({
  training,
  race,
  compact,
}: {
  training: Province['military']['training'];
  race: string;
  compact: boolean;
}) {
  const planner = useMemo(() => buildTrainingPlannerData(training, race), [training, race]);
  const labelMinWidth = compact ? 90 : 120;
  const visibleDays = (days: number[], totals: Record<number, number>) => (compact ? days.filter((d) => totals[d] > 0) : days);
  const { scrollRef, canScrollLeft, canScrollRight } = useHorizontalScrollAffordance<HTMLDivElement>();

  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
      <div className="px-3 py-1.5" style={{ backgroundColor: COLORS.sectionHeader }}>
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Training</span>
      </div>

      {planner ? (
        <div className="relative">
          {canScrollLeft && (
            <div
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 z-10 pointer-events-none"
              style={{ width: 24, background: `linear-gradient(to right, ${COLORS.card}, transparent)` }}
            />
          )}
          {canScrollRight && (
            <div
              aria-hidden="true"
              className="absolute right-0 top-0 bottom-0 z-10 pointer-events-none"
              style={{ width: 24, background: `linear-gradient(to left, ${COLORS.card}, transparent)` }}
            />
          )}
          <div ref={scrollRef} style={{ overflowX: 'auto' }} tabIndex={0} role="region" aria-label="Training schedule by day">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <colgroup>
              <col style={{ minWidth: labelMinWidth }} />
              {visibleDays(planner.days, planner.totals).map((day) => (
                <col key={day} style={{ minWidth: '28px' }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  className="text-left px-2 py-1 text-[10px] uppercase tracking-wide"
                  style={{
                    color: COLORS.text.tertiary,
                    borderBottom: `1px solid ${COLORS.border}`,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: COLORS.sectionHeader,
                  }}
                >
                  Days until completed
                </th>
                {visibleDays(planner.days, planner.totals).map((day) => (
                  <th key={day} className="text-center py-1 text-[10px] font-mono" style={{ color: COLORS.text.tertiary, borderBottom: `1px solid ${COLORS.border}` }}>
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {planner.units.map((u) => (
                <tr key={u.unitType} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td
                    className="px-2 py-0.5 text-sm"
                    style={{ color: COLORS.text.primary, position: 'sticky', left: 0, zIndex: 1, backgroundColor: COLORS.card }}
                  >
                    {u.label}
                  </td>
                  {visibleDays(planner.days, planner.totals).map((day) => {
                    const incoming = u.byDay[day];
                    return (
                      <td key={day} className="text-center py-0.5 font-mono text-xs" style={{ color: incoming ? COLORS.text.secondary : COLORS.text.tertiary }}>
                        {incoming ? Math.round(incoming) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td
                  className="px-2 py-0.5 text-xs font-semibold"
                  style={{ color: COLORS.text.primary, position: 'sticky', left: 0, zIndex: 1, backgroundColor: COLORS.sectionHeader }}
                >
                  Total
                </td>
                {visibleDays(planner.days, planner.totals).map((day) => (
                  <td key={day} className="text-center py-0.5 font-mono text-xs font-semibold" style={{ color: COLORS.text.primary, backgroundColor: COLORS.sectionHeader }}>
                    {planner.totals[day] ? Math.round(planner.totals[day]) : ''}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="px-3 py-3 text-sm" style={{ color: COLORS.text.tertiary }}>No units in training.</div>
      )}
    </div>
  );
}
